import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { useAdminTrpc } from 'shared/api'
import { Input } from 'shared/components/ui/input'
import { CopyChip } from 'shared/components/CopyChip'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { parseClientFilters, replaceClientFilters } from '../lib/state'
import { Checkbox } from 'shared/components/ui/checkbox'

export function Clients() {
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const { search, tezOnly } = parseClientFilters(params)
	const { t, prefixT } = useTranslate('pages.adminClients')
	const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useInfiniteQuery({
			...trpc.users.List.infiniteQueryOptions(
				{ search: search || undefined, hasTezId: tezOnly || undefined },
				{ getNextPageParam: (last) => last.nextCursor, initialCursor: 0 },
			),
		})
	const clients = useMemo(
		() => (data?.pages ?? []).flatMap((p) => p.items),
		[data],
	)
	const total = data?.pages[0]?.total

	// Infinite scroll: fetch the next page when the sentinel nears the viewport.
	const sentinelRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage)
					void fetchNextPage()
			},
			{ rootMargin: '600px' },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [hasNextPage, isFetchingNextPage, fetchNextPage])

	return (
		<div className='flex flex-col gap-3 p-4'>
			<h1 className='text-lg font-semibold'>
				{prefixT('title')}{total !== undefined ? ` (${total})` : ''}
			</h1>
			<Input
				type='search'
				placeholder={prefixT('searchPlaceholder')}
				value={search}
				onChange={(e) =>
					replaceClientFilters(setParams, { search: e.target.value })
				}
			/>
			<label className='flex items-center gap-2 text-sm'>
				<Checkbox
					checked={tezOnly}
					onCheckedChange={(checked) =>
						replaceClientFilters(setParams, { tezOnly: !!checked })
					}
				/>
				{prefixT('tezIdOnly')}
			</label>
			<div className='flex flex-col divide-y rounded-lg border'>
				{clients.map((c) => (
					<div
						key={c._id}
						role='button'
						tabIndex={0}
						className='flex cursor-pointer items-center gap-2 p-3 text-left text-sm'
						onClick={() => navigate(`/admin/clients/${c._id}`)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') navigate(`/admin/clients/${c._id}`)
						}}
					>
						<span className='flex-1'>
							<span className='block font-medium'>{c.name}</span>
							<span className='block text-muted-foreground'>
								{c.username ? `@${c.username}` : (c.telegramId ?? '')}
							</span>
						</span>
						{c.tezId && <CopyChip value={c.tezId} />}
						<span className='text-right text-xs text-muted-foreground'>
							{prefixT('ordersSummary', {
								count: String(c.purchaseCount),
								amount: t('common.amountUzs', {
									amount: formatUzsAmount(c.netPaidUzs),
								}),
							})}
						</span>
					</div>
				))}
			</div>
			<div ref={sentinelRef} />
		</div>
	)
}
